import { useState } from 'preact/hooks';
import { type PurchaseEntry, type SaleEntry } from '@khata/core';
import {
  Empty,
  MonthPicker,
  PageHead,
  Row,
  SearchBox,
  ShowMore,
  VoidedToggle,
  periodLabel,
} from '../components/index.js';
import { dShort, num, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { accountName, linesSummary, partyName } from '../lib/labels.js';
import { listEntries, matches, monthsWithEntries, periodRange } from '../lib/lists.js';
import { usePaged } from '../lib/paging.js';
import { listPeriod, openSheet, showVoided } from '../lib/ui.js';

export function Tx(props: { kind: 'sale' | 'purchase' }) {
  const s = state.value;
  const m = master.value;
  const isSale = props.kind === 'sale';
  const [q, setQ] = useState('');
  const pick = (e: { type: string }): e is SaleEntry | PurchaseEntry => e.type === props.kind;
  const range = periodRange(listPeriod.value, today.value);
  const inPeriod = listEntries(s, entries.value, pick, showVoided.value, range);
  const rows = q.trim()
    ? inPeriod.filter(({ entry: e }) =>
        matches(
          `${partyName(m, e.partyId, isSale ? t('walkIn') : t('noVendor'))} #${e.no} ${linesSummary(m, e)} ${e.note}`,
          q,
        ),
      )
    : inPeriod;
  const live = rows.filter((r) => !r.voided);
  const total = live.reduce((tt, r) => tt + r.entry.total, 0);
  const paged = usePaged(rows, listPeriod.value.kind === 'all' ? 300 : 1000);

  return (
    <>
      <PageHead
        title={isSale ? t('tx_sales') : t('tx_purchases')}
        sub={t('list_inPeriod', {
          n: num(live.length),
          total: taka(total as never),
          period: periodLabel(),
        })}
      >
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => openSheet({ kind: props.kind })}
        >
          {t('tx_new')}
        </button>
      </PageHead>
      <SearchBox value={q} onInput={setQ} placeholder={t('search_tx')} />
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <VoidedToggle />
      <div class="list">
        {rows.length === 0 && <Empty>{isSale ? t('tx_noSales') : t('tx_noPurchases')}</Empty>}
        {paged.visible.map(({ entry: e, voided }) => {
          const due = e.total - e.paid;
          return (
            <Row
              key={e.id}
              title={
                <>
                  {partyName(m, e.partyId, isSale ? t('walkIn') : t('noVendor'))}{' '}
                  <span class="ref">#{num(e.no)}</span>
                </>
              }
              sub={`${dShort(e.date)} · ${linesSummary(m, e)}`}
              amount={taka(e.total)}
              amountSub={
                voided
                  ? t('voided')
                  : due > 0
                    ? `${isSale ? t('bokeya') : t('due')} ${taka(due as never)}`
                    : t('tx_paidVia', { account: accountName(m, e.accountId) })
              }
              amountSubTone={!voided && due > 0 ? 'bad' : 'muted'}
              strike={voided}
              onClick={() => openSheet({ kind: 'entry', id: e.id })}
            />
          );
        })}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
